import * as React from "react";
import {IFeesTreeNode} from "../../model/Portfolio.model";
import {Body, Cell, Header, HeaderCell, HeaderRow, Row, Table} from "@table-library/react-table-library/table";
import {PLAN_TREE_THEME} from "./portfolioUtil";
import {CellTree, Tree, useTree} from "@table-library/react-table-library/tree";
import {Link} from "react-router-dom";
import {useEffect, useState} from "react";
import {
    IPortfolioFeesTreeNode,
    ABOVE_BELOW_UNIVERSE_MEDIAN,
    PEER_UNIVERSE_MEDIAN_COLOR_MAP
} from "../../api/PortfolioApi";
import {FeesPopup} from "./FeesPopup";
import {Action, IdState, MiddlewareFunction} from "@table-library/react-table-library/types/common";
import {
    setFeesSelectedProductAction,
    updateFeesExpandedRowsAction
} from "./PortfolioActions";
import {useDispatch, useSelector} from "react-redux";
import {scrollToElement} from "../../utils/browserUtil";
import {feesExpandedRows, feesSelectedProduct} from "./PortfolioMapSelectors";
import {buildFeeTree} from "./portfolioPlanTreeUtil";
import {IApplicationRootState} from "../../../applicationState";
import {AnyThunkDispatch} from "../../../commonTypes";
import Popover from "@mui/material/Popover";

export interface IFeesTabProps {
    portfolioFees: IPortfolioFeesTreeNode[]
}

export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_1 = "The investment fee analysis reported in the table above measures only the relative fee level of your current plan offerings as compared to fee medians from style, size, and vehicle-specific peer universes, which Aon Investments believes is an appropriate comparison. Determining whether an investment's expense is reasonable depends on a variety of factors including the level of returns achieved and the method with which administration and operational expenses are paid. Understanding and evaluating the total cost of plan operations, including both investment and administration fees is a critical analysis that should be completed periodically.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_2 = "Expense Ratio is intended to represent a fund's total expense ratio which may include investment management fees, 12b-1 fees, custodial fees, trustee fees, revenue sharing components and/or charges for fund or plan administration. Expense ratios shown reflect the current fee and may not match your quarterly investment report.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_3 = "Fee universes are provided and calculated by eVestment Alliance, as of prior year end. Fund expense information has been sourced from eVestment for all asset classes except the stable value and target date fund universes, which uses Morningstar databases. Since fund expense ratios do not change frequently, Aon Investments believes this study to be a relevant comparison for up to a year, at which time it will be updated.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_4 = "For complete information on methodologies, contact your Aon Investments consultant.";

export const FeesTab: React.FC<IFeesTabProps> = (props) => {
    const [nodes, setNodes] = useState<IFeesTreeNode[]>([]);
    const [displayPopup, setDisplayPopup] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number>(-1);
    const [importantNoteOpen, setImportantNoteOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

    const expandedRows = useSelector((state: IApplicationRootState) => feesExpandedRows(state));
    const selectedProduct = useSelector((state: IApplicationRootState) => feesSelectedProduct(state));

    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        if (selectedProduct) {
            setTimeout(() => {
                scrollToElement(`#link-${selectedProduct}`, 100, true);
            }, (10));
        }
    }, []);

    useEffect(() => {
        setNodes(buildFeeTree(props.portfolioFees));
    }, [props]);

    const closePopup = () => {
        setDisplayPopup(false);
        setSelectedId(-1);
        setAnchorEl(null);
    };

    const data = {nodes};

    const renderName = (item: IFeesTreeNode) => {
        return item.backstopId
            ? <Link to={`/products/${item.backstopId}`}
                    onClick={() => dispatch(setFeesSelectedProductAction(item.id))}
                    id={`link-${item.id}`}>
                {item.name}
            </Link>
            : item.name;
    };

    const expandCollapse = () => {
        tree.fns.onToggleAll({isCarryForward: false, isPartialToAll: true});
    };

    const onTreeChange = (action: Action, state: IdState) => {
        const expandedRowsToStore = state.ids.map((id) => id as number);
        dispatch(updateFeesExpandedRowsAction(expandedRowsToStore));
    };

    const tree: Tree<IFeesTreeNode> = useTree(data,
        // library's MiddlewareFunction types state as the wider Record; our handler reads the narrower IdState
        {onChange: onTreeChange as unknown as MiddlewareFunction, state: {ids: expandedRows}},
        {treeIcon: {
                iconRight: (
                    <div className="far fa-chevron-right fa-chevron-tree"/>
                ),
                iconDown: (
                    <div className="far fa-chevron-down  fa-chevron-tree"/>
                ),
            }},
    );

    const getCellStyle = (value: string | null) => {
        if (value === null) return {};
        return PEER_UNIVERSE_MEDIAN_COLOR_MAP[value] || {};
    };

    const renderPopup = () => {
        const selectedNode = props.portfolioFees.find(node => node.id === selectedId)!;

        return <Popover
            id={"fees-popover"}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        >
            <FeesPopup selectedNode={selectedNode} closePopup={closePopup}/>
        </Popover>;
    };

    const renderTree = () => {
        return (
            <Table data={data}
                   tree={tree}
                   theme={PLAN_TREE_THEME("30% 30% 9% 8% 11% 12%")}
                   layout={{custom: true}}>
                {(nodes: IFeesTreeNode[]) => (
                    <>
                        <Header>
                            <HeaderRow>
                                <HeaderCell>Category</HeaderCell>
                                <HeaderCell className={"fees-tab__peer-universe-name"}>Peer Universe</HeaderCell>
                                <HeaderCell>Net Expense<br/> Ratio (%)</HeaderCell>
                                <HeaderCell>Revenue<br/> Share (%)</HeaderCell>
                                <HeaderCell>Effective Net<br/> Expense Ratio (%)</HeaderCell>
                                <HeaderCell>Above/Below Peer<br/> Universe Median</HeaderCell>
                            </HeaderRow>
                        </Header>

                        <Body>
                            {nodes.map((item: IFeesTreeNode) => (
                                <Row key={item.id} item={item}
                                     className={item.nodes && item.nodes.length > 0 ? "portfolio__parent-node" : ""}>
                                    <CellTree item={item}>
                                        {renderName(item)}
                                    </CellTree>
                                    <Cell className={"fees-tab__peer-universe-name"}>{item.peerUniverseName}</Cell>
                                    <Cell>{item.netExpenseRatio?.toFixed(3)}</Cell>
                                    <Cell>{item.revenueShare?.toFixed(3)}</Cell>
                                    <Cell>
                                        {item.effectiveNetExpenseRatio?.toFixed(3)}
                                    </Cell>
                                    <Cell className={"fees-tab__peer-universe-median"}>
                                        {item.peerUniverseMedian !== ABOVE_BELOW_UNIVERSE_MEDIAN.UNKNOWN
                                            ? <div  className={"clickable"} style={getCellStyle(item.peerUniverseMedian)}
                                                 onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                     setDisplayPopup(!displayPopup);
                                                     setSelectedId(item.id);
                                                     setAnchorEl(event.currentTarget);
                                                }
                                                 }>{item.peerUniverseMedian}</div>
                                            : <div style={getCellStyle(item.peerUniverseMedian)}>
                                                {item.peerUniverseMedian}
                                            </div>
                                        }
                                    </Cell>
                                </Row>
                            ))}
                        </Body>
                    </>
                )}
            </Table>
        );
    };

    const renderImportantNoteText = () => {
        return <div className={"fees-tab__important-note-text"} data-testid="fees-tab__important-note-text">
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_1}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_2}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_3}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_4}</p>
        </div>;
    };

    const renderImportantNote = () => {
        return <div className={"fees-tab__important-note-container"}>
          <span className="fees-tab__important-note-toggle clickable link-16"
                data-testid="fees-tab__important-note-toggle"
                onClick={() => setImportantNoteOpen(!importantNoteOpen)}>
                    Important Note
                    <span className={importantNoteOpen ? "fal fa-angle-up" : "fal fa-angle-down"}
                          data-testid={"fees-tab__important-note-icon"}/>
            </span>
            {importantNoteOpen && renderImportantNoteText()}
        </div>;
    };

    return <div className="portfolio__tree-container" data-testid="fees-tab__container">
        <div className="portfolio__header-container">
            <div className="portfolio__expand-collapse-container">
                <div className="portfolio__expand-collapse-text link-14 clickable fees" onClick={expandCollapse}>
                    Expand/Collapse All
                </div>
            </div>
        </div>
        <div className="portfolio__tree-table-container">
            {renderTree()}
            { displayPopup && renderPopup()}
        </div>
        {renderImportantNote()}
    </div>;
};